// frontend/src/pages/DownloadEpisodes.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '../components/Breadcrumb';
import { listProjects, getProject, listEpisodes, listShots, listAllEpisodeVersions, batchSubtitleEraseStatus } from '../api';
import { getAIClipTask, listAIClipTasks } from '../api/aiClip';
import type { Project, Episode, SubtitleEraseTask } from '../types';
import {
  collectAIClipTasks,
  collectEpisodeTasks,
  listAllAIClipTasks,
  parseEpisodeRange,
  formatBytes,
  formatDuration,
  formatRate,
} from '../lib/dramaDownload/taskCollection';
import { isFsAccessSupported, pickDownloadDirectory } from '../lib/dramaDownload/fsAccess';
import { runDownloads } from '../lib/dramaDownload/downloader';
import type { DownloadStats, DownloadTask, ProgressEvent, TaskStatus } from '../lib/dramaDownload/types';

interface Row { key: string; localPath: string; status: TaskStatus; bytes?: number; totalBytes?: number; error?: string; }

const SEARCH_DEBOUNCE_MS = 350;
// 后端 /subtitle-erase/batch-status 单次上限 200 个 versionId（超出返回 400）
const ERASE_STATUS_BATCH_SIZE = 200;

async function fetchEraseTasks(versionIds: string[]): Promise<Record<string, SubtitleEraseTask>> {
  let result: Record<string, SubtitleEraseTask> = {};
  for (let i = 0; i < versionIds.length; i += ERASE_STATUS_BATCH_SIZE) {
    const chunk = versionIds.slice(i, i + ERASE_STATUS_BATCH_SIZE);
    try {
      const { data } = await batchSubtitleEraseStatus(chunk);
      result = { ...result, ...(data ?? {}) };
    } catch { /* 该批擦除状态取失败：对应版本只下普通版，不阻塞整集 */ }
  }
  return result;
}

async function resolveAIClipArtifactURL(
  episodeId: string,
  taskId: string,
  artifact: 'video' | 'premiere',
): Promise<string> {
  const detail = await getAIClipTask(episodeId, taskId);
  const url = artifact === 'video' ? detail.videoPlayUrl : detail.premiereDownloadUrl;
  if (!url) throw new Error(artifact === 'video' ? 'AI clip video unavailable' : 'Premiere XML unavailable');
  return url;
}

export default function DownloadEpisodes() {
  const { t } = useTranslation('profile');
  const supported = useMemo(() => isFsAccessSupported(), []);

  const [projects, setProjects] = useState<Project[]>([]);
  const [nameInput, setNameInput] = useState('');
  const [idInput, setIdInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const reqIdRef = useRef(0);

  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [selectedEpisodeIds, setSelectedEpisodeIds] = useState<Set<string>>(new Set());

  const [dirHandle, setDirHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [dirName, setDirName] = useState('');

  const [preparing, setPreparing] = useState(false);
  const [running, setRunning] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [stats, setStats] = useState<DownloadStats | null>(null);
  const [durationMs, setDurationMs] = useState(0); // 完成汇总耗时
  const [nowTick, setNowTick] = useState(0);       // 触发进度条速度/ETA 刷新
  const abortRef = useRef<AbortController | null>(null);
  const startedAtRef = useRef(0);

  // 运行中每秒滴答一次，让已下大小/速度/剩余时间实时更新（下载回调只在字节里程碑触发）
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  // 搜索项目：名称模糊 or ID 精确；不在挂载时预加载全部项目（reqId 防竞态覆盖）
  const runSearch = useCallback(async (name: string, id: string) => {
    const n = name.trim();
    const pid = id.trim();
    if (!n && !pid) { setProjects([]); setHasQueried(false); setSearching(false); return; }
    const myId = ++reqIdRef.current;
    setSearching(true);
    try {
      const found = pid
        ? await getProject(pid).then(p => (p ? [p] : []))
        : (await listProjects(0, n)).projects;
      if (myId === reqIdRef.current) setProjects(found);
    } catch {
      if (myId === reqIdRef.current) setProjects([]);
    } finally {
      if (myId === reqIdRef.current) { setSearching(false); setHasQueried(true); }
    }
  }, []);

  useEffect(() => {
    if (!supported) return;
    const timer = window.setTimeout(() => { runSearch(nameInput, idInput); }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [supported, nameInput, idInput, runSearch]);

  const chooseProject = useCallback(async (p: Project) => {
    setSelectedProject(p);
    setEpisodes([]);
    setSelectedEpisodeIds(new Set());
    try {
      const eps = await listEpisodes(p.id);
      setEpisodes([...eps].sort((a, b) => a.index - b.index));
    } catch { setEpisodes([]); }
  }, []);

  const toggleEpisode = useCallback((id: string) => {
    setSelectedEpisodeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedEpisodeIds(new Set(episodes.map(e => e.id)));
  }, [episodes]);

  const applyRange = useCallback((text: string) => {
    const set = parseEpisodeRange(text);
    if (set === null) { setSelectedEpisodeIds(new Set(episodes.map(e => e.id))); return; }
    setSelectedEpisodeIds(new Set(episodes.filter(e => set.has(e.index)).map(e => e.id)));
  }, [episodes]);

  // 必须在按钮 click 回调里同步调用（保持用户手势激活）
  const chooseDir = useCallback(async () => {
    try {
      const handle = await pickDownloadDirectory();
      setDirHandle(handle);
      setDirName(handle.name);
    } catch { /* 用户取消选择：保持原状 */ }
  }, []);

  const start = useCallback(async () => {
    if (!dirHandle || selectedEpisodeIds.size === 0) return;
    setPreparing(true);
    setRows([]);
    setStats(null);
    try {
      const chosen = episodes.filter(e => selectedEpisodeIds.has(e.id));
      const tasks: DownloadTask[] = [];
      for (const ep of chosen) {
        try {
          const [shots, versions] = await Promise.all([listShots(ep.id), listAllEpisodeVersions(ep.id)]);
          const versionIds = Object.values(versions).flat().map(v => v.id);
          const eraseTasks = await fetchEraseTasks(versionIds);
          tasks.push(...collectEpisodeTasks(ep.name, shots, versions, eraseTasks));
        } catch { /* 分镜取数失败不影响同集 AI 剪辑产物 */ }
        try {
          const clipHistory = await listAllAIClipTasks(ep.id, listAIClipTasks);
          tasks.push(...collectAIClipTasks(ep.id, ep.name, clipHistory, resolveAIClipArtifactURL));
        } catch { /* AI 剪辑历史取数失败不影响同集分镜视频 */ }
      }
      setPreparing(false);
      setRows(tasks.map(t => ({ key: `${t.shotId}_${t.versionId}`, localPath: t.localPath, status: 'pending' as TaskStatus })));
      const ac = new AbortController();
      abortRef.current = ac;
      startedAtRef.current = Date.now();
      setNowTick(startedAtRef.current);
      setRunning(true);
      const onProgress = (e: ProgressEvent) => {
        const key = `${e.task.shotId}_${e.task.versionId}`;
        setRows(prev => prev.map(r => r.key === key ? { ...r, status: e.status, bytes: e.bytes, totalBytes: e.totalBytes, error: e.error } : r));
      };
      const finalStats = await runDownloads({ root: dirHandle, tasks, signal: ac.signal, onProgress });
      setDurationMs(Date.now() - startedAtRef.current);
      setStats(finalStats);
    } catch { /* 运行/取数整体失败：保留已渲染的行，stats 留空不显示汇总 */ }
    finally {
      // 无论成功或异常都复位，避免 running 卡死（进度轮询/取消按钮不复位、无汇总）
      setPreparing(false);
      setRunning(false);
    }
  }, [dirHandle, selectedEpisodeIds, episodes]);

  const cancel = useCallback(() => { abortRef.current?.abort(); }, []);

  if (!supported) {
    return (
      <div className="container" style={{ paddingTop: 24, paddingBottom: 48 }}>
        <div className="card" style={{ maxWidth: 520, margin: '48px auto', padding: 28, textAlign: 'center' }}>
          <div style={badgeStyle}>{downloadIcon}</div>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>{t('dlEpisodes.title')}</h2>
          <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 14, lineHeight: 1.6 }}>{t('dlEpisodes.unsupported')}</p>
        </div>
      </div>
    );
  }

  const doneCount = rows.filter(r => r.status !== 'pending' && r.status !== 'downloading').length;
  // 进度指标（spec §3⑤）：X/Y · % · 已下大小 · 速度 · 剩余时间
  const cumBytes = rows.reduce((n, r) => n + (r.bytes ?? 0), 0);
  const knownTotalBytes = rows.reduce((n, r) => n + (r.totalBytes ?? 0), 0);
  const pct = rows.length > 0 ? Math.round((doneCount / rows.length) * 100) : 0;
  const elapsedSec = running ? Math.max(0, (nowTick - startedAtRef.current) / 1000) : 0;
  const speed = elapsedSec > 0 ? cumBytes / elapsedSec : 0;
  const etaSec = speed > 0 && knownTotalBytes > cumBytes ? (knownTotalBytes - cumBytes) / speed : null;

  return (
    <div className="container" style={{ paddingTop: 24, paddingBottom: 48 }}>
      <Breadcrumb items={[{ label: t('download.breadcrumbHome'), to: '/projects' }, { label: t('dlEpisodes.title') }]} />

      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={badgeStyle}>{downloadIcon}</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 600 }}>{t('dlEpisodes.title')}</h2>
          <p style={{ margin: 0, color: 'var(--text-3)', fontSize: 14 }}>{t('dlEpisodes.subtitle')}</p>
        </div>

        {/* Step 1: 选项目 */}
        <section className="card" style={{ padding: 20 }}>
          <StepHeader n={1} title={t('dlEpisodes.pickProject')} />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              className="input"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') runSearch(nameInput, idInput); }}
              placeholder={t('dlEpisodes.searchProject')}
              style={{ flex: '1 1 200px' }}
            />
            <input
              className="input"
              value={idInput}
              onChange={e => setIdInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') runSearch(nameInput, idInput); }}
              placeholder={t('dlEpisodes.searchProjectId')}
              style={{ flex: '1 1 200px', fontFamily: 'monospace', fontSize: 13 }}
            />
          </div>

          <div style={{ marginTop: 14 }}>
            {searching ? (
              <div style={hintRowStyle}><span className="loading-spinner" style={{ borderTopColor: 'var(--primary)', borderColor: 'var(--border)' }} />{t('dlEpisodes.searching')}</div>
            ) : !hasQueried ? (
              <div style={{ ...hintRowStyle, color: 'var(--text-3)' }}>{t('dlEpisodes.searchHint')}</div>
            ) : projects.length === 0 ? (
              <div style={{ ...hintRowStyle, color: 'var(--text-3)' }}>{t('dlEpisodes.noProjects')}</div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {projects.map(p => {
                  const active = selectedProject?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => chooseProject(p)}
                      style={projectRowStyle(active)}
                    >
                      <span style={{ fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <code style={{ fontSize: 11, color: 'var(--text-3)' }}>{p.id.slice(0, 8)}</code>
                        {active && <span style={{ color: 'var(--primary)', fontWeight: 700 }}>✓</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Step 2: 选剧集 */}
        {selectedProject && (
          <section className="card" style={{ padding: 20 }}>
            <StepHeader
              n={2}
              title={t('dlEpisodes.pickEpisodes')}
              right={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="btn btn-sm btn-secondary" onClick={selectAll}>{t('dlEpisodes.selectAll')}</button>
                  <input
                    className="input"
                    onChange={e => applyRange(e.target.value)}
                    placeholder={t('dlEpisodes.rangeHint')}
                    style={{ width: 150, height: 32, fontSize: 13, padding: '4px 10px' }}
                  />
                </div>
              }
            />
            <div style={{ maxHeight: 220, overflow: 'auto', display: 'grid', gap: 2 }}>
              {episodes.map(ep => {
                const checked = selectedEpisodeIds.has(ep.id);
                return (
                  <label key={ep.id} style={episodeRowStyle(checked)}>
                    <input type="checkbox" checked={checked} onChange={() => toggleEpisode(ep.id)} />
                    <span style={{ color: 'var(--text-3)', fontSize: 12, minWidth: 28 }}>{ep.index}</span>
                    <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ep.name}</span>
                  </label>
                );
              })}
            </div>
          </section>
        )}

        {/* Step 3: 下载到本地 */}
        {selectedProject && (
          <section className="card" style={{ padding: 20 }}>
            <StepHeader n={3} title={t('dlEpisodes.saveTo')} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={chooseDir}>{t('dlEpisodes.chooseDir')}</button>
              {dirName && <span style={{ color: 'var(--text-2)', fontSize: 13 }}>{t('dlEpisodes.chosenDir', { name: dirName })}</span>}
              <div style={{ flex: 1 }} />
              {!running && (
                <button className="btn btn-primary" onClick={start} disabled={!dirHandle || selectedEpisodeIds.size === 0 || preparing}>
                  {preparing && <span className="loading-spinner" />}{t('dlEpisodes.start')}
                </button>
              )}
              {running && <button className="btn btn-secondary" onClick={cancel}>{t('dlEpisodes.cancel')}</button>}
            </div>
            {preparing && <p style={{ color: 'var(--text-3)', fontSize: 13, margin: '12px 0 0' }}>{t('dlEpisodes.preparing')}</p>}
          </section>
        )}

        {/* 进度 */}
        {rows.length > 0 && (
          <section className="card" style={{ padding: 20 }}>
            {/* 总进度条（隐式 role="progressbar"）— spec §3⑤ */}
            <progress value={doneCount} max={rows.length} style={progressBarStyle} />
            <div style={{ fontSize: 13, color: 'var(--text-2)', margin: '10px 0 12px' }}>
              {stats
                ? t('dlEpisodes.done', {
                    total: stats.total, success: stats.success, skipped: stats.skipped,
                    noVideo: stats.noVideo, failed: stats.failed,
                    size: formatBytes(stats.bytes), duration: formatDuration(durationMs / 1000),
                  })
                : t('dlEpisodes.overall', {
                    done: doneCount, total: rows.length, pct,
                    size: formatBytes(cumBytes), speed: formatRate(speed),
                    eta: etaSec !== null ? formatDuration(etaSec) : '—',
                    success: rows.filter(r => r.status === 'done').length,
                    skipped: rows.filter(r => r.status === 'skipped').length,
                    noVideo: rows.filter(r => r.status === 'no-video').length,
                    failed: rows.filter(r => r.status === 'failed').length,
                  })}
            </div>
            <div style={{ maxHeight: 320, overflow: 'auto', display: 'grid', gap: 1, fontSize: 12, fontFamily: 'monospace' }}>
              {rows.map(r => (
                <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 8px', borderRadius: 6, background: 'var(--bg-2, #f4f4f5)' }}>
                  <span style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.localPath}</span>
                  <span style={{ color: statusColor(r.status), flexShrink: 0 }}>{statusLabel(r.status, t)}{r.error ? ` (${r.error})` : ''}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ─── 小型展示组件 / 样式 ───

interface StepHeaderProps { n: number; title: string; right?: React.ReactNode; }
function StepHeader({ n, title, right }: StepHeaderProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <span style={{
        width: 22, height: 22, borderRadius: '50%', background: 'var(--primary-light)', color: 'var(--primary)',
        fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>{n}</span>
      <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>{title}</h3>
      {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
    </div>
  );
}

const badgeStyle: React.CSSProperties = {
  width: 56, height: 56, borderRadius: 16, background: 'var(--primary-light)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
};

const hintRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 2px', fontSize: 13,
};

const progressBarStyle: React.CSSProperties = { width: '100%', height: 8 };

function projectRowStyle(active: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
    background: active ? 'var(--primary-light)' : 'var(--surface)',
    transition: 'border-color var(--transition-fast), background var(--transition-fast)',
  };
}

function episodeRowStyle(checked: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
    background: checked ? 'var(--primary-light)' : 'transparent',
  };
}

function statusColor(status: TaskStatus): string {
  switch (status) {
    case 'done': return 'var(--success)';
    case 'failed': return 'var(--danger)';
    case 'downloading': return 'var(--primary)';
    default: return 'var(--text-3)';
  }
}

const downloadIcon = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

function statusLabel(status: TaskStatus, t: (k: string) => string): string {
  switch (status) {
    case 'done': return `✓ ${t('dlEpisodes.statusDone')}`;
    case 'skipped': return `⊘ ${t('dlEpisodes.statusSkipped')}`;
    case 'no-video': return `⊘ ${t('dlEpisodes.statusNoVideo')}`;
    case 'failed': return `✗ ${t('dlEpisodes.statusFailed')}`;
    case 'downloading': return '…';
    default: return '';
  }
}
